import { useStableCallback } from '@main/shared/browser';
import { isNonNullable, NAMESPACE } from '@main/shared/utils';
import { ColumnDef, ColumnOrderState, OnChangeFn } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { useTableSettingsNamespace } from '../../utils';
import { getLegacyColumnIdMap, resolveLegacyColumnIdList } from './legacy-column-ids';
import { actionsColumnId } from './table-column-ids';

type ColumnOrderStateLocalStorage = {
  columnOrder: ColumnOrderState;
  defaultColumnOrder: ColumnOrderState;
};

const isNotActionsColumn = ({ id }: { id?: string }) => id !== actionsColumnId;

export function useTableColumnOrder<T>(
  columns: ColumnDef<T>[],
  isColumnsLoading: boolean,
  tableName: string,
  columnVisibility: Record<string, boolean> = {},
  defaultColumnVisibility: Record<string, boolean>,
  tableId?: string,
  saveDebounceMs = 300,
) {
  const settingsNamespace = useTableSettingsNamespace();

  return useTableColumnOrderForNamespace(
    columns,
    isColumnsLoading,
    tableName,
    settingsNamespace,
    columnVisibility,
    defaultColumnVisibility,
    tableId,
    saveDebounceMs,
  );
}

export function useTableColumnOrderForNamespace<T>(
  columns: ColumnDef<T>[],
  isColumnsLoading: boolean,
  tableName: string,
  settingsNamespace: string | undefined,
  columnVisibility: Record<string, boolean> = {},
  defaultColumnVisibility: Record<string, boolean>,
  tableId?: string,
  saveDebounceMs = 300,
) {
  const keyPostfix = settingsNamespace
    ? `:${settingsNamespace}:column-ordering-v2`
    : `:column-ordering`;
  const generalKey = `${NAMESPACE}${tableName}${keyPostfix}`;
  const columnOrderingKey = tableId
    ? `${NAMESPACE}${tableName}:${tableId}${keyPostfix}`
    : generalKey;

  const defaultColumnOrder = useMemo(
    () =>
      columns
        .filter(isNonNullable)
        .filter(isNotActionsColumn)
        .sort((a, b) => {
          const aVisible = a?.id ? (defaultColumnVisibility[a?.id] ?? true) : true;
          const bVisible = b?.id ? (defaultColumnVisibility[b?.id] ?? true) : true;
          if (aVisible === bVisible) return 0;
          return aVisible ? -1 : 1;
        })
        .map((column) => column.id)
        .filter(isNonNullable),
    [defaultColumnVisibility, columns],
  );
  const initialState = useMemo(() => {
    const localStorageItem =
      localStorage.getItem(columnOrderingKey) || localStorage.getItem(generalKey);
    const localStorageParsed: unknown = localStorageItem && JSON.parse(localStorageItem);
    if (isColumnOrderState(localStorageParsed)) {
      return localStorageParsed;
    }

    return { columnOrder: defaultColumnOrder, defaultColumnOrder };
  }, [columnOrderingKey, defaultColumnOrder, generalKey]);

  const [columnOrderState, setColumnOrderState] = useState(initialState);

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = useStableCallback(
    (update: ColumnOrderState | ((prevState: ColumnOrderState) => ColumnOrderState)) => {
      if (Array.isArray(update) && update.length === 0) {
        setColumnOrderState((state) => ({ ...state, columnOrder: defaultColumnOrder }));
        return;
      }
      setColumnOrderState((state) => ({
        ...state,
        columnOrder: typeof update === 'function' ? update(state.columnOrder) : update,
      }));
    },
  );

  const columnsWithoutActions = useMemo(
    () => columns.filter(isNonNullable).filter(isNotActionsColumn),
    [columns],
  );
  useEffect(() => {
    if (isColumnsLoading) {
      return;
    }

    // Move saved legacy ids to the column ids first, so a renamed column keeps its position
    // instead of being pruned and re-added at its default position.
    const resolvedColumnOrder = resolveLegacyColumnIdList(
      columnOrderState.columnOrder,
      getLegacyColumnIdMap(columns),
    );
    const allIds = columnsWithoutActions.map((column) => column.id);
    const newColumns = allIds
      .filter(isNonNullable)
      .filter((id) => !resolvedColumnOrder.includes(id));
    const filteredColumns = resolvedColumnOrder.filter((id) => allIds.includes(id));
    const newColumnOrder = [...filteredColumns];
    for (const id of newColumns) {
      const defaultIndex = defaultColumnOrder.indexOf(id);
      const insertAt = newColumnOrder.findIndex(
        (orderedId) => defaultColumnOrder.indexOf(orderedId) > defaultIndex,
      );
      newColumnOrder.splice(insertAt < 0 ? newColumnOrder.length : insertAt, 0, id);
    }
    const newColumnOrderHash = newColumnOrder.toSorted().join('/');
    const columnOrderHash = columnOrderState.columnOrder.toSorted().join('/');
    if (
      resolvedColumnOrder !== columnOrderState.columnOrder ||
      newColumnOrderHash !== columnOrderHash
    ) {
      // This handles a couple scenarios:
      // 1. Asynchronous loading of columns
      // 2. Columns being added or removed (e.g hardcoded or custom fields)
      // In these cases, we want to sync the state, maintaining the order where possible

      setColumnOrderState((state) => ({
        ...state,
        columnOrder: newColumnOrder,
      }));
    }
  }, [
    columnOrderState,
    columnOrderState.columnOrder,
    columns,
    columnsWithoutActions,
    defaultColumnOrder,
    isColumnsLoading,
  ]);
  useEffect(() => {
    setColumnOrderState((state) => ({ ...state, defaultColumnOrder }));
  }, [defaultColumnOrder]);
  useEffect(() => {
    const timeout = setTimeout(
      () => localStorage.setItem(columnOrderingKey, JSON.stringify(columnOrderState)),
      saveDebounceMs,
    );
    return () => clearTimeout(timeout);
  }, [columnOrderState, columnOrderingKey, saveDebounceMs]);

  const visibleColumnOrderState = columnOrderState.columnOrder.filter(
    (columnId) => columnVisibility[columnId] ?? true,
  );
  const defaultVisibleColumnOrderState = columnOrderState.defaultColumnOrder.filter(
    (columnId) => defaultColumnVisibility[columnId] ?? true,
  );
  const hasColumnOrderChanged = !(
    visibleColumnOrderState.length === defaultVisibleColumnOrderState.length &&
    visibleColumnOrderState.every((id, index) => id === defaultVisibleColumnOrderState[index])
  );

  return {
    columnOrder: columnOrderState.columnOrder,
    hasColumnOrderChanged,
    onColumnOrderChange,
  };
}

function isColumnOrderState(obj: unknown): obj is ColumnOrderStateLocalStorage {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'columnOrder' in obj &&
    Array.isArray(obj.columnOrder) &&
    'defaultColumnOrder' in obj &&
    Array.isArray(obj.defaultColumnOrder)
  );
}
